import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Modal } from "../../components/ui/Modal";
import { Input } from "../../components/ui/Input";
import { MultiSelect } from "../../components/ui/MultiSelect";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import { customizationAssetApi, CustomizationAsset } from "../../services/customizationAssetApi";
import { getImageUrl } from "../../utils/imageUtils";
import { FaTrash, FaEdit, FaPlus, FaFont } from "react-icons/fa";

// Helper function to get all available fonts for the designer
const getAllAvailableFonts = (): Array<{ value: string; label: string }> => {
  const systemFonts = [
    "Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana", 
    "Georgia", "Palatino", "Garamond", "Comic Sans MS", "Trebuchet MS", 
    "Impact", "Lucida Console", "Tahoma"
  ];
  
  const typefaceFonts = [
    "Almarai", "Anton", "Barlow Condensed", "BBH Bartle", "Cairo", "Caveat",
    "Dancing Script", "Edu SA Beginner", "Follow", "Great Vibes", "Josefin Sans Thin",
    "Lobster", "Lobster Two", "Noto Sans Symbols", "Orbitron", "Pacifico",
    "Satisfy", "Shadows Into Light", "Smooch Sans Thin", "Space Grotesk Light", "Tajawal"
  ];
  
  const googleFonts = [
    "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Oswald", "Raleway",
    "Poppins", "Source Sans Pro", "Playfair Display", "Merriweather", "Ubuntu",
    "Dancing Script", "Pacifico", "Lobster", "Bebas Neue", "Bangers", "Righteous",
    "Fredoka One", "Comfortaa", "Quicksand", "Caveat", "Kalam", "Permanent Marker",
    "Shadows Into Light", "Indie Flower", "Crimson Text", "Fira Sans", "PT Sans",
    "Nunito", "Work Sans", "Rubik", "Noto Sans", "Mukta", "DM Sans", "Manrope",
    "Outfit", "Plus Jakarta Sans", "Trirong",
    // Arabic Fonts
    "Amiri", "Changa", "El Messiri", "Harmattan", "Lateef", "Markazi Text",
    "Reem Kufi", "Scheherazade New", "Noto Sans Arabic", "Noto Naskh Arabic",
    "Noto Kufi Arabic", "IBM Plex Sans Arabic", "Cairo", "Tajawal", "Almarai"
  ];
  
  // Combine all fonts and remove duplicates
  const allFontsSet = new Set([...systemFonts, ...typefaceFonts, ...googleFonts]);
  const allFonts = Array.from(allFontsSet);
  
  return allFonts.map(font => ({ value: font, label: font })).sort((a, b) => 
    a.label.localeCompare(b.label)
  );
};

export const CustomizationAssets: React.FC = () => {
  const { t } = useTranslation();
  const { api } = useApi();
  const { store, refreshStore } = useStore();
  const [assets, setAssets] = useState<CustomizationAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"all" | "background" | "shape" | "fonts">("all");
  const [allowedDesignerFonts, setAllowedDesignerFonts] = useState<string[]>([]);
  const [savingFonts, setSavingFonts] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<CustomizationAsset | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState<"background" | "shape">("background");
  const [uploadCategory, setUploadCategory] = useState("");

  const fetchAssets = useCallback(async () => {
    // Skip fetching assets when fonts tab is selected (fonts aren't assets)
    if (filterType === "fonts") {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const type = filterType === "all" ? undefined : (filterType as "background" | "shape" | undefined);
      const data = await customizationAssetApi.getAssets(type);
      setAssets(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch assets");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Load allowed fonts from store
  useEffect(() => {
    if (store?.allowedDesignerFonts) {
      setAllowedDesignerFonts(store.allowedDesignerFonts);
    } else {
      setAllowedDesignerFonts([]);
    }
  }, [store?.allowedDesignerFonts]);

  const handleSaveFonts = async () => {
    try {
      setSavingFonts(true);
      setError(null);
      await api.put("/store/theme-settings", {
        allowedDesignerFonts: allowedDesignerFonts.length > 0 ? allowedDesignerFonts : [],
      });
      setSuccess("Font settings saved successfully");
      await refreshStore();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save font settings");
    } finally {
      setSavingFonts(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setError("Please select a file");
      return;
    }

    if (!uploadType) {
      setError("Please select asset type");
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await customizationAssetApi.uploadAsset({
        file: uploadFile,
        type: uploadType,
        name: uploadName || undefined,
        category: uploadCategory || undefined,
      });
      setSuccess("Asset uploaded successfully");
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadName("");
      setUploadCategory("");
      setUploadType("background");
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload asset");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) {
      return;
    }

    try {
      setError(null);
      await customizationAssetApi.deleteAsset(id);
      setSuccess("Asset deleted successfully");
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete asset");
    }
  };

  const handleEdit = (asset: CustomizationAsset) => {
    setEditingAsset(asset);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingAsset) return;

    try {
      setError(null);
      await customizationAssetApi.updateAsset(editingAsset._id, {
        name: editingAsset.name,
        category: editingAsset.category,
        isActive: editingAsset.isActive,
      });
      setSuccess("Asset updated successfully");
      setShowEditModal(false);
      setEditingAsset(null);
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update asset");
    }
  };

  const filteredAssets = assets.filter((asset) => {
    if (filterType === "all") return true;
    return asset.type === filterType;
  });

  const backgrounds = filteredAssets.filter((a) => a.type === "background");
  const shapes = filteredAssets.filter((a) => a.type === "shape");

  return (
    <Container>
      <Stack spacing="lg">
        <div className="flex items-center justify-between">
          <Heading size="xl">Customization Assets</Heading>
          {filterType !== "fonts" && (
            <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2">
              <FaPlus className="w-4 h-4" />
              Upload Asset
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setFilterType("all")}
            className={`px-4 py-2 font-medium ${
              filterType === "all"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            All ({assets.length})
          </button>
          <button
            onClick={() => setFilterType("background")}
            className={`px-4 py-2 font-medium ${
              filterType === "background"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Backgrounds ({backgrounds.length})
          </button>
          <button
            onClick={() => setFilterType("shape")}
            className={`px-4 py-2 font-medium ${
              filterType === "shape"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Shapes ({shapes.length})
          </button>
          <button
            onClick={() => setFilterType("fonts")}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${
              filterType === "fonts"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FaFont className="w-4 h-4" />
            Fonts ({allowedDesignerFonts.length > 0 ? allowedDesignerFonts.length : "All"})
          </button>
        </div>

        {filterType === "fonts" ? (
          <Card padding="lg">
            <Stack spacing="md">
              <div>
                <Heading size="md" className="mb-2">Available Fonts for 2D Designer</Heading>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Select which fonts users can use in the 2D designer. Leave empty to allow all fonts.
                </p>
              </div>
              <MultiSelect
                label="Select Allowed Fonts"
                options={getAllAvailableFonts()}
                value={allowedDesignerFonts}
                onChange={setAllowedDesignerFonts}
                placeholder="Select fonts to allow (leave empty to allow all fonts)"
                helperText="If no fonts are selected, all fonts will be available to users in the 2D designer."
                isFontSelector={true}
              />
              {allowedDesignerFonts.length > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {allowedDesignerFonts.length} font{allowedDesignerFonts.length !== 1 ? "s" : ""} selected. Users will only be able to use these fonts in the 2D designer.
                </p>
              )}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveFonts} 
                  disabled={savingFonts}
                  className="flex items-center gap-2"
                >
                  {savingFonts ? "Saving..." : "Save Font Settings"}
                </Button>
              </div>
            </Stack>
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAssets.map((asset) => (
              <Card key={asset._id} padding="md">
                <div className="space-y-3">
                  <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                    <img
                      src={getImageUrl(asset.url, api.baseURL)}
                      alt={asset.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{asset.name}</h3>
                    <p className="text-xs text-gray-500">
                      {asset.type} {asset.category && `• ${asset.category}`}
                    </p>
                    <p className="text-xs text-gray-400">{asset.fileType.toUpperCase()}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(asset)}
                      className="flex-1 flex items-center justify-center gap-2"
                    >
                      <FaEdit className="w-3 h-3" />
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(asset._id)}
                      className="flex items-center justify-center gap-2"
                    >
                      <FaTrash className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {filteredAssets.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                No assets found. Upload your first asset to get started.
              </div>
            )}
          </div>
        )}
      </Stack>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadFile(null);
          setUploadName("");
          setUploadCategory("");
        }}
        title="Upload Asset"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
                setUploadName("");
                setUploadCategory("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading}>
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        }
      >
        <Stack spacing="md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Asset Type</label>
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as "background" | "shape")}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="background">Background</option>
              <option value="shape">Shape</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
            <input
              type="file"
              accept="image/png,image/svg+xml,image/jpeg,image/jpg,image/webp"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <Input
            label={t("admin.customizationAssets.nameOptional")}
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            placeholder={t("common.placeholders.leaveEmptyForFilename")}
          />

          {uploadType === "shape" && (
            <Input
              label={t("admin.customizationAssets.categoryOptional")}
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              placeholder={t("common.placeholders.animalsBirdsExample")}
            />
          )}
        </Stack>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingAsset(null);
        }}
        title="Edit Asset"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </div>
        }
      >
        {editingAsset && (
          <Stack spacing="md">
            <Input
              label={t("admin.customizationAssets.nameOptional")}
              value={editingAsset.name}
              onChange={(e) =>
                setEditingAsset({ ...editingAsset, name: e.target.value })
              }
            />

            {editingAsset.type === "shape" && (
              <Input
                label={t("admin.customizationAssets.categoryOptional")}
                value={editingAsset.category || ""}
                onChange={(e) =>
                  setEditingAsset({ ...editingAsset, category: e.target.value })
                }
              />
            )}

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingAsset.isActive}
                  onChange={(e) =>
                    setEditingAsset({ ...editingAsset, isActive: e.target.checked })
                  }
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

